import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * The app's searchable picker (2026-09-25, PO ruling 4): a text field that filters an always-open list under it,
 * WAI-ARIA's "combobox with listbox popup" pattern. The focus stays in the field; ↑/↓ move the active row
 * (`aria-activedescendant`), Enter chooses it, and Escape is left to whatever holds the picker — a Radix popover or a
 * `ModalDialog` — so it closes that, as everywhere else. The caller filters (`normalizeSearch` for accents) and
 * orders the rows; the primitive only draws and steers them. `ChoiceList` is the same list without the field, for a
 * short fixed set (Trạng thái, Ưu tiên).
 *
 * Rows are 40 px under a mouse and 44 px under a thumb, like every other target.
 */

export type ComboOption = {
  /** Unique within the list; part of the row's DOM id. */
  key: string;
  /** What `onChoose` receives. */
  value: string;
  label: ReactNode;
  /** Group heading the row sits under; consecutive rows with the same group share one heading. */
  group?: string;
  /** Muted text before the label: a card's ancestors ("Việc lớn › "). */
  path?: string;
  /** Tree depth, indented 16 px per level. */
  depth?: number;
  /** Shown, never chosen (a person who left). */
  disabled?: boolean;
  /** The value the control holds now: a check mark. */
  current?: boolean;
  /** Extra classes for the row. */
  className?: string;
};

const ROW = cn(
  'flex min-h-10 pointer-coarse:min-h-11 w-full cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
  'data-[active]:bg-[hsl(var(--h-muted))] data-[active]:text-[hsl(var(--h-card-foreground))]',
  'aria-disabled:cursor-not-allowed aria-disabled:opacity-55',
);
const HEADING = 'px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--h-muted-foreground))]';

type Grouped = { group: string | undefined; rows: { option: ComboOption; index: number }[] }[];
function grouped(options: readonly ComboOption[]): Grouped {
  const out: Grouped = [];
  options.forEach((option, index) => {
    const last = out.at(-1);
    if (last !== undefined && last.group === option.group) last.rows.push({ option, index });
    else out.push({ group: option.group, rows: [{ option, index }] });
  });
  return out;
}

const firstEnabled = (options: readonly ComboOption[], from: number, step: 1 | -1): number => {
  for (let i = from; i >= 0 && i < options.length; i += step) if (!options[i]!.disabled) return i;
  return -1;
};

/** Keeps an active row that the list still holds, else the current value, else the first choosable row. */
function useActive(options: readonly ComboOption[], resetKey: string) {
  const [active, setActive] = useState<string | null>(null);
  const reset = useRef(resetKey);
  useEffect(() => {
    if (reset.current === resetKey) return;
    reset.current = resetKey;
    setActive(null);
  }, [resetKey]);
  let index = active === null ? -1 : options.findIndex((option) => option.key === active && !option.disabled);
  // Before any search the current value is where the list opens; a search starts at its best match.
  if (index < 0 && resetKey === '') index = options.findIndex((option) => option.current && !option.disabled);
  if (index < 0) index = firstEnabled(options, 0, 1);
  const move = (step: 1 | -1) => {
    if (options.length === 0) return;
    let next = firstEnabled(options, index + step, step);
    if (next < 0) next = firstEnabled(options, step === 1 ? 0 : options.length - 1, step);
    if (next >= 0) setActive(options[next]!.key);
  };
  return { index, move, setActive };
}

function Rows({ listId, label, options, activeIndex, onChoose, onHover, empty, className, focusable }: {
  listId: string; label: string; options: readonly ComboOption[]; activeIndex: number;
  onChoose: (option: ComboOption) => void; onHover: (key: string) => void; empty?: ReactNode; className?: string;
  /** The list itself holds the focus (no field): it carries the active row. */
  focusable?: boolean;
}) {
  const list = useRef<HTMLDivElement>(null);
  const activeKey = options[activeIndex]?.key;
  useEffect(() => {
    if (activeKey === undefined) return;
    list.current?.querySelector<HTMLElement>('[data-active]')?.scrollIntoView?.({ block: 'nearest' });
  }, [activeKey]);
  const row = ({ option, index }: { option: ComboOption; index: number }) => (
    <div
      key={option.key} id={`${listId}-${index}`} role="option"
      aria-selected={index === activeIndex}
      aria-disabled={option.disabled ? true : undefined}
      aria-current={option.current ? true : undefined}
      data-active={index === activeIndex ? '' : undefined}
      className={cn(ROW, option.className)}
      style={option.depth ? { paddingLeft: `${8 + option.depth * 16}px` } : undefined}
      // The field keeps the focus: a row is pressed, never focused.
      onMouseDown={(event) => event.preventDefault()}
      onMouseMove={() => { if (!option.disabled && index !== activeIndex) onHover(option.key); }}
      onClick={() => { if (!option.disabled) onChoose(option); }}
    >
      <span className="min-w-0 flex-1 break-words">
        {option.path && <span className="text-[hsl(var(--h-muted-foreground))]">{option.path}</span>}
        {option.label}
      </span>
      {option.current && <Check aria-hidden="true" className="size-4 shrink-0" />}
    </div>
  );
  return (
    <div
      ref={list} id={listId} role="listbox" aria-label={label} tabIndex={focusable ? 0 : -1}
      aria-activedescendant={focusable && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
      className={cn('flex max-h-80 flex-col overflow-y-auto overscroll-contain', className)}
    >
      {options.length === 0 && empty !== undefined && (
        <p className="px-2 py-3 text-sm text-[hsl(var(--h-muted-foreground))]">{empty}</p>
      )}
      {grouped(options).map((block, at) => (block.group === undefined
        ? block.rows.map(row)
        : (
          <div key={`${block.group}-${at}`} role="group" aria-labelledby={`${listId}-g${at}`}>
            <div id={`${listId}-g${at}`} role="presentation" className={HEADING}>{block.group}</div>
            {block.rows.map(row)}
          </div>
        )))}
    </div>
  );
}

export type ComboboxProps = {
  /** The field's and the list's accessible name. */
  label: string;
  placeholder?: string;
  query: string;
  onQueryChange: (query: string) => void;
  /** Already filtered and ordered by the caller. */
  options: readonly ComboOption[];
  onChoose: (value: string, option: ComboOption) => void;
  /** Shown when no row matches. */
  empty?: ReactNode;
  inputRef?: RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  className?: string;
  listClassName?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
};

export function Combobox({
  label, placeholder, query, onQueryChange, options, onChoose, empty, inputRef, autoFocus, className, listClassName,
  'aria-invalid': invalid, 'aria-describedby': describedBy,
}: ComboboxProps) {
  const listId = useId();
  const { index, move, setActive } = useActive(options, query);
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' && !(event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      const option = options[index];
      if (option !== undefined && !option.disabled) onChoose(option.value, option);
    }
  };
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <input
        ref={inputRef} type="text" role="combobox" autoComplete="off" spellCheck={false}
        aria-label={label} aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={index >= 0 ? `${listId}-${index}` : undefined}
        aria-invalid={invalid ? true : undefined} aria-describedby={describedBy}
        autoFocus={autoFocus}
        placeholder={placeholder} value={query} onChange={(event) => onQueryChange(event.target.value)} onKeyDown={onKeyDown}
        className={cn(
          'flex h-11 pointer-fine:h-9 w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none aria-invalid:border-destructive',
        )}
      />
      <Rows
        listId={listId} label={label} options={options} activeIndex={index} empty={empty} className={listClassName}
        onHover={setActive} onChoose={(option) => onChoose(option.value, option)}
      />
    </div>
  );
}

/**
 * The same list without a field, for a handful of fixed choices in a popover: the list itself takes the focus and
 * the arrow keys.
 */
export function ChoiceList({ label, options, onChoose, className, autoFocus = true }: {
  label: string; options: readonly ComboOption[]; onChoose: (value: string) => void; className?: string; autoFocus?: boolean;
}) {
  const listId = useId();
  const { index, move, setActive } = useActive(options, '');
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoFocus) box.current?.querySelector<HTMLElement>('[role="listbox"]')?.focus();
  }, [autoFocus]);
  return (
    <div
      ref={box} className={className}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          move(event.key === 'ArrowDown' ? 1 : -1);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const option = options[index];
          if (option !== undefined && !option.disabled) onChoose(option.value);
        }
      }}
    >
      <Rows
        listId={listId} label={label} options={options} activeIndex={index} focusable
        onHover={setActive} onChoose={(option) => onChoose(option.value)}
        className="rounded-md p-1 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      />
    </div>
  );
}

export type Choice = { value: string; label: string; display?: ReactNode };

/**
 * A pill or rail row whose value is one of a few fixed choices (Trạng thái, Ưu tiên): the trigger shows the value,
 * a popover (the shared `h-pop` motion) holds the list. Choosing closes it and gives the focus back to the trigger;
 * choosing the value it already has changes nothing.
 */
export function ChoicePopover({
  label, value, choices, onChange, className, children, align = 'start', disabled, onOpenChange,
}: {
  label: string;
  value: string;
  choices: readonly Choice[];
  onChange: (value: string) => void;
  className?: string;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const change = (next: boolean) => { setOpen(next); onOpenChange?.(next); };
  const current = choices.find((choice) => choice.value === value);
  return (
    <Popover open={open} onOpenChange={change}>
      <PopoverTrigger
        type="button" data-field-trigger="" className={className} disabled={disabled}
        aria-label={`${label}: ${current?.label ?? '—'}`}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        aria-label={label} align={align} className="w-56 p-1"
        // The list takes the focus itself, on its current row.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <ChoiceList
          label={label}
          options={choices.map((choice) => ({
            key: choice.value, value: choice.value, label: choice.display ?? choice.label, current: choice.value === value,
          }))}
          onChoose={(next) => {
            change(false);
            if (next !== value) onChange(next);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
